/**
 * Translation keys (register in backend / CMS):
 * - sync.tab.integrationstests
 * - sync.integrationstests.col.step, .status, .start, .end, .actions, .date, .startedby
 * - sync.integrationstests.status.inprogress, .completed, .failed
 * - sync.integrationstests.errorlog — display name `Error log · Latest run · {{duration}}`
 * - sync.integrationstests.run, sync.integrationstests.download
 */
import * as ko from "knockout";
import PromiseRequest from "../../../req/PromiseRequest";
import {PLEASE_WAIT_MESSAGE} from "../../../constants/Language";
import {createErrorMessage} from "../../../util/misc";
import {global} from "../../../appContext";
import {ICompany, ICompanySyncstep, IMessageData, IRouteDefinitionParams} from "../../../index";
import {convertFromServerString} from "../../../util/datetime";
import {nsSync} from "../../../util/nsSync";
import {nsRoute} from "../../../util/route";
import {nsFile} from "../status-v2/nsFile";
import detailsHtml from './details.html?raw';
import {isIntegrationTestStepVisible} from "./testableSyncsteps";
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import {
    canDownloadTestRun,
    formatTestRunDate,
    formatTestRunDuration,
    testRunStatus,
    TTestRunStatus,
} from "./runStatus";

export const template = detailsHtml;

const POLL_MS = 5000;

interface ITestIntegrationRun {
    id: number
    syncstepid: number
    targetvalue: string
    lasterror: string
    exported: string | null
    registered: string
    registeredby: number
    registeredbyemail?: string
}

interface IIntegrationTestsParent {
    downloadRun(run: ITestIntegrationRun): Promise<void>
    triggerRun(syncstepId: number): Promise<unknown>
    isTriggering(syncstepId: number): boolean
    rememberExpanded(syncstepId: number, expanded: boolean): void
    readonly messageData: KnockoutObservable<IMessageData | null>
}

const STATUS_TEXT: Record<TTestRunStatus, string> = {
    inprogress: 'sync.integrationstests.status.inprogress',
    completed: 'sync.integrationstests.status.completed',
    failed: 'sync.integrationstests.status.failed',
};

// Shared .ds-status-pill tones (sass/component/_status-pill.scss); same mapping as sync-cycle step badges.
const STATUS_PILL_CLS: Record<TTestRunStatus, string> = {
    inprogress: 'ds-status-pill--info ds-status-pill--with-dot ds-status-pill--pulse',
    completed: 'ds-status-pill--success ds-status-pill--with-dot',
    failed: 'ds-status-pill--danger ds-status-pill--with-dot',
};

class HistoryRowVm {
    readonly isDownloading = ko.observable(false);

    constructor(
        readonly run: ITestIntegrationRun,
        private readonly parentView: IIntegrationTestsParent,
    ) {
    }

    status(): TTestRunStatus {
        return testRunStatus(this.run);
    }

    showStatusPill(): boolean {
        return this.status() !== 'inprogress';
    }

    statusText(): string {
        return STATUS_TEXT[this.status()];
    }

    statusPillCls(): string {
        return STATUS_PILL_CLS[this.status()];
    }

    dateText(): string {
        return formatTestRunDate(this.run.registered);
    }

    startedByText(): string {
        const email = (this.run.registeredbyemail ?? '').trim();
        return email || '—';
    }

    canDownload(): boolean {
        return canDownloadTestRun(this.run) && !this.isDownloading();
    }

    onDownload = () => {
        if (!canDownloadTestRun(this.run) || this.isDownloading()) {
            return;
        }
        this.isDownloading(true);
        this.parentView.downloadRun(this.run)
            .finally(() => this.isDownloading(false));
    };
}

class StepRowVm {
    readonly isExpanded: KnockoutObservable<boolean>;
    readonly isDownloading = ko.observable(false);
    readonly history: HistoryRowVm[];

    constructor(
        readonly companySyncstep: ICompanySyncstep,
        runs: ITestIntegrationRun[],
        expanded: boolean,
        private readonly parentView: IIntegrationTestsParent,
    ) {
        this.isExpanded = ko.observable(expanded && runs.length > 0);
        this.history = runs.map((run) => new HistoryRowVm(run, parentView));
    }

    get syncstepId(): number {
        return this.companySyncstep.syncstep.id;
    }

    name(): string {
        return nsSync.getSyncstepName(this.companySyncstep.syncstep);
    }

    latest(): HistoryRowVm | null {
        return this.history[0] ?? null;
    }

    status(): TTestRunStatus | null {
        const latest = this.latest();
        return latest ? latest.status() : null;
    }

    hasRuns(): boolean {
        return this.history.length > 0;
    }

    statusText(): string {
        const status = this.status();
        return status ? STATUS_TEXT[status] : '';
    }

    statusPillCls(): string {
        const status = this.status();
        return status ? STATUS_PILL_CLS[status] : '';
    }

    startText(): string {
        const latest = this.latest();
        return latest ? formatTestRunDate(latest.run.registered) : '—';
    }

    endText(): string {
        const latest = this.latest();
        if (!latest || latest.status() === 'inprogress') {
            return '—';
        }
        return formatTestRunDate(latest.run.exported);
    }

    showError(): boolean {
        return this.status() === 'failed';
    }

    errorText(): string {
        return (this.latest()?.run.lasterror ?? '').trim();
    }

    errorLogLabel() {
        const latest = this.latest();
        return {
            k: 'sync.integrationstests.errorlog',
            r: {
                duration: formatTestRunDuration(latest?.run.registered, latest?.run.exported),
            },
        };
    }

    canDownloadLatest(): boolean {
        const latest = this.latest();
        return !!latest && canDownloadTestRun(latest.run) && !this.isDownloading();
    }

    canRun(): boolean {
        return this.status() !== 'inprogress' && !this.parentView.isTriggering(this.syncstepId);
    }

    onRowClick = () => {
        if (!this.hasRuns()) {
            return;
        }
        const next = !this.isExpanded();
        this.isExpanded(next);
        this.parentView.rememberExpanded(this.syncstepId, next);
    };

    onDownloadLatest = () => {
        const latest = this.latest();
        if (!latest || !canDownloadTestRun(latest.run) || this.isDownloading()) {
            return;
        }
        this.isDownloading(true);
        this.parentView.downloadRun(latest.run)
            .finally(() => this.isDownloading(false));
    };

    onRun = () => {
        if (!this.canRun()) {
            return;
        }
        this.parentView.triggerRun(this.syncstepId);
    };
}

export class viewModel extends ParsedRouteDataDecorator implements IIntegrationTestsParent {
    readonly steps = ko.observableArray<StepRowVm>([]);
    readonly messageData = ko.observable<IMessageData | null>(null);
    readonly isLoading = ko.observable(true);

    private company: ICompany | null = null;
    private companySteps: ICompanySyncstep[] = [];
    private readonly expandedIds = new Set<number>();
    private pollTimer: number | undefined;
    private disposed = false;
    private readonly reqCompany = new PromiseRequest('getcompany');
    private readonly reqSteps = new PromiseRequest('getcompanysyncsteps');
    private readonly reqRuns = new PromiseRequest('gettestintegrations');
    /** Survives applyRuns replacing row view-models. Run stays disabled until the request settles. */
    private readonly triggeringIds = ko.observableArray<number>([]);
    private readonly triggerRequests = new Set<PromiseRequest>();

    constructor(params: IRouteDefinitionParams) {
        super(params);
        this.loadAll();
    }

    rememberExpanded(syncstepId: number, expanded: boolean) {
        if (expanded) {
            this.expandedIds.add(syncstepId);
        } else {
            this.expandedIds.delete(syncstepId);
        }
    }

    isTriggering(syncstepId: number): boolean {
        return this.triggeringIds().includes(syncstepId);
    }

    triggerRun(syncstepId: number) {
        if (this.disposed || this.isTriggering(syncstepId)) {
            return Promise.resolve();
        }
        // Own request per click. A shared PromiseRequest.abort drops the previous
        // promise without settling it, so the first Run would stay pending.
        this.triggeringIds.push(syncstepId);
        window.clearTimeout(this.pollTimer);
        const request = new PromiseRequest('triggertestintegration');
        this.triggerRequests.add(request);
        return request
            .request({syncstepid: syncstepId})
            .then(() => {
                if (!this.disposed) {
                    return this.loadRuns(true);
                }
            })
            .catch((reason: string) => {
                if (!this.disposed) {
                    this.messageData(createErrorMessage(String(reason)));
                }
            })
            .finally(() => {
                this.triggerRequests.delete(request);
                this.triggeringIds.remove(syncstepId);
                if (!this.disposed) {
                    this.armPoll();
                }
            });
    }

    downloadRun(run: ITestIntegrationRun) {
        return nsFile.downloadFile({
            filename: `eAdm_${run.id}.zip`,
            params: {
                id: run.id,
                filepath: run.targetvalue,
                orgnr: global.login.selectedOrganizationId(),
            },
            servicename: 'getfiles',
        }).catch((reason: string) => {
            this.messageData(createErrorMessage(String(reason)));
        });
    }

    override dispose() {
        this.disposed = true;
        window.clearTimeout(this.pollTimer);
        this.reqCompany.abort();
        this.reqSteps.abort();
        this.reqRuns.abort();
        this.triggerRequests.forEach((request) => request.abort());
        this.triggerRequests.clear();
        super.dispose();
    }

    private loadAll() {
        this.isLoading(true);
        this.messageData({message: PLEASE_WAIT_MESSAGE});
        this.reqSteps.abort();
        const companyPromise: Promise<ICompany | null> = this.__requestRouteData
            ? this.__requestRouteData.request().then((data: ICompany[]) => data?.[0] ?? null)
            : this.reqCompany.request().then((data: ICompany[]) => data?.[0] ?? null);
        return Promise.all([companyPromise, this.reqSteps.request()])
            .then(([company, steps]: [ICompany | null, ICompanySyncstep[]]) => {
                this.company = company;
                this.companySteps = steps ?? [];
                return this.reqRuns.request();
            })
            .then((runs: ITestIntegrationRun[]) => {
                if (this.disposed) {
                    return;
                }
                this.applyRuns(runs ?? []);
                this.messageData(null);
            })
            .catch((reason: string) => {
                if (this.disposed) {
                    return;
                }
                this.messageData(createErrorMessage(String(reason)));
            })
            .finally(() => {
                this.isLoading(false);
                this.armPoll();
            });
    }

    private loadRuns(silent: boolean): Promise<void> {
        const joinedInFlight = this.reqRuns.isRequesting();
        return this.reqRuns
            .request()
            .then((runs: ITestIntegrationRun[]) => {
                if (this.disposed) {
                    return;
                }
                if (joinedInFlight) {
                    return this.loadRuns(silent);
                }
                this.applyRuns(runs ?? []);
                if (!silent || this.messageData()?.type !== 'danger') {
                    this.messageData(null);
                }
            })
            .catch((reason: string) => {
                if (this.disposed) {
                    return;
                }
                this.messageData(createErrorMessage(String(reason)));
            })
            .finally(() => this.armPoll());
    }

    private applyRuns(runs: ITestIntegrationRun[]) {
        const byStep = new Map<number, ITestIntegrationRun[]>();
        runs.forEach((run) => {
            const list = byStep.get(run.syncstepid) ?? [];
            list.push(run);
            byStep.set(run.syncstepid, list);
        });

        const seen = new Set<number>();
        const rows: StepRowVm[] = [];
        const ordered = this.companySteps.slice().sort((a, b) => a.order - b.order);
        ordered.forEach((step) => {
            const syncstepId = step.syncstep?.id;
            if (!syncstepId || !isIntegrationTestStepVisible(syncstepId, this.company) || seen.has(syncstepId)) {
                return;
            }
            seen.add(syncstepId);
            const stepRuns = (byStep.get(syncstepId) ?? []).slice().sort((a, b) => {
                const aTime = convertFromServerString(a.registered).valueOf() || 0;
                const bTime = convertFromServerString(b.registered).valueOf() || 0;
                if (bTime !== aTime) {
                    return bTime - aTime;
                }
                return b.id - a.id;
            });
            rows.push(new StepRowVm(step, stepRuns, this.expandedIds.has(syncstepId), this));
        });
        this.steps(rows);
    }

    private armPoll() {
        if (this.disposed) {
            return;
        }
        window.clearTimeout(this.pollTimer);
        this.pollTimer = undefined;
        const waiting = this.steps().some((step) => step.status() === 'inprogress');
        if (!waiting) {
            return;
        }
        this.pollTimer = window.setTimeout(() => {
            this.loadRuns(true);
        }, POLL_MS);
    }
}
